import { useMemo } from "react";
import { Card, CardContent, Typography, Label } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { CopyButton } from "@/components/copyButton";
import { ROCodeEditor } from "@/components/codeEditor";
import { getBackendUrl, getTenant, isFreeStack } from "@/helpers/tenant";
import { useAppState } from "@/modules/app/useAppContext";
import { IntegrationDetails } from "@/types/integration";
import { default as InfoGray } from "@/assets/icons/info_gray.svg?react";

interface DbtCoreInstructionsCardProps {
  integration: IntegrationDetails;
}

export const DbtCoreInstructionsCard = ({
  integration,
}: DbtCoreInstructionsCardProps): JSX.Element => {
  const tenant = getTenant();
  const backendURL = getBackendUrl();
  const { dbtWorkloadEnabled, loggedinUser } = useAppState();
  const auth_token = loggedinUser?.auth_token;
  const isFree = isFreeStack();

  const command = useMemo(() => {
    const commonCommands = ["dbt compile", "dbt docs generate", "dbt source freshness"];
    const baseCommand = `datapilot dbt onboard --backend-url ${backendURL} --token ${auth_token} --instance-name ${tenant} --manifest-path ./target/manifest.json --catalog-path ./target/catalog.json --run-results-path ./target/run_results.json --semantic-manifest-path ./target/semantic_manifest.json --sources-path ./target/sources.json`;
    let completeCommand = "";
    if (integration.integration_type === "dbt_core") {
      completeCommand = `${baseCommand} --dbt_core_integration_id ${integration.id} --dbt_core_integration_environment ${integration.environment_name}`;
    } else if (integration.integration_type === "dbt_cloud") {
      completeCommand = `${baseCommand} --dbt_integration_id ${integration.id} --dbt_integration_environment ${integration.environment_name}`;
    }
    return commonCommands.concat(completeCommand).join("\n");
  }, [integration, backendURL, auth_token, tenant]);

  const queryTag = useMemo(() => {
    return JSON.stringify(
      {
        dbt_integration_id: parseInt(integration.id, 10),
        dbt_integration_environment: integration.environment_name,
      },
      null,
      2
    );
  }, [integration]);

  return (
    <Card className="al-overflow-hidden al-rounded-lg al-border al-border-gray-200">
      <CardContent className="!al-p-4">
        <Typography weight="regular" size="regular" className="al-mb-3">
          Instructions to Connect
        </Typography>

        <div className="al-h-[300px] al-overflow-y-auto al-overflow-x-hidden">
          {command && (
            <>
              <div className="al-mb-1">
                <Label className="al-text-sm al-font-medium">Command</Label>
                <section aria-label="Command code editor">
                  <ROCodeEditor lineNumbers height="6em" value={command} showCopy={false} />
                </section>
              </div>

              <Stack
                direction="row"
                align="start"
                gap="xs"
                className="al-mb-3 al-p-2 al-bg-gray-50 al-rounded"
              >
                <InfoGray className="al-w-4 al-h-4 al-mt-0.5 al-flex-shrink-0" />
                <Typography size="sm" className="al-text-muted-foreground">
                  You need to run these commands from the dbt project root directory. If you are not
                  using default manifest and catalog paths, you will need to update the paths in the
                  command.
                </Typography>
              </Stack>

              <div className="al-mb-4">
                <CopyButton value={command} label="Copy Command" copiedLabel="Command Copied" />
              </div>
            </>
          )}

          {!isFree && dbtWorkloadEnabled && queryTag.length > 0 && (
            <section aria-label="Query tag configuration">
              <div className="al-mb-1">
                <Label className="al-text-sm al-font-medium">Enable Query Tags</Label>
                <section aria-label="Query tag code editor">
                  <ROCodeEditor
                    lineNumbers={false}
                    height="5em"
                    value={queryTag}
                    showCopy={false}
                  />
                </section>
              </div>

              <Stack
                direction="row"
                align="start"
                gap="xs"
                className="al-mb-3 al-p-2 al-bg-gray-50 al-rounded"
              >
                <InfoGray className="al-w-4 al-h-4 al-mt-0.5 al-flex-shrink-0" />
                <Stack direction="column" gap="xs">
                  <Typography size="sm" className="al-text-muted-foreground">
                    Add this to your query_tag parameter in profiles.yml or dbt Cloud connection
                    settings.
                  </Typography>
                  <a
                    className="al-text-brand al-text-sm"
                    rel="noreferrer"
                    href="https://docs.altimate.ai/docs/dbt-core"
                    target="_blank"
                  >
                    View complete setup guide
                  </a>
                </Stack>
              </Stack>

              <div>
                <CopyButton
                  value={JSON.stringify(JSON.parse(queryTag))}
                  label="Copy Query Tag"
                  copiedLabel="Query Tag Copied"
                />
              </div>
            </section>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
